import { logger } from "logger";
import config from "../../config/config";
import { useAuthContext } from "../Auth";
import { FC, useEffect, useState } from "react";
import { StreamChat } from "stream-chat";
import { StreamChatGenerics } from "views/chats/types";
import { Chat } from "stream-chat-react";
import { PartnerFeatures } from "shared/types/partner";

export const ChatProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [client, setClient] = useState<null | StreamChat<StreamChatGenerics>>(
    null
  );
  const { auth, partner, partnerFeatures } = useAuthContext();
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);

  useEffect(() => {
    if (
      !auth?.id ||
      !auth.stream_chat_id ||
      !auth.stream_chat_token ||
      !partner ||
      !hasChat
    )
      return;

    let cancelled = false;
    const streamClient = StreamChat.getInstance(config.streamApiKey, {
      timeout: 9000
    });

    // Already connected as this user — reuse without reconnecting
    if (streamClient.userID === auth.stream_chat_id) {
      setClient(streamClient);
      return;
    }

    const connect = async () => {
      // Connected as a different user — await full disconnect first
      if (streamClient.userID) await streamClient.disconnectUser();

      if (cancelled) return;

      await streamClient.connectUser(
        { id: auth.stream_chat_id!, name: auth.name },
        auth.stream_chat_token!
      );
      if (!cancelled) setClient(streamClient);
    };

    connect().catch((e) => {
      if (!cancelled) logger.error("Error connecting to Stream Chat", e);
    });

    return () => {
      cancelled = true;
    };
  }, [
    auth?.id,
    auth?.stream_chat_id,
    auth?.stream_chat_token,
    partner?.name,
    hasChat
  ]);

  // Disconnect only when the provider unmounts (logout / app teardown)
  useEffect(
    () => () => {
      StreamChat.getInstance(config.streamApiKey).disconnectUser();
    },
    []
  );

  if (!client) return children;

  return (
    <Chat client={client} theme="messaging light">
      {children}
    </Chat>
  );
};
